import { useMemo } from 'react';
import { format } from 'date-fns';
import { useEffectiveBookingsForDate } from '@/hooks/useRecurringBookings';
import { useBookings } from '@/hooks/useBookings';

export interface BookingConflict {
  type: 'regular' | 'recurring';
  clientName: string;
  timeSlot: string;
  workoutType: string;
  message: string;
}

export const useBookingValidation = (selectedDate: Date) => {
  const { data: effectiveBookings = [] } = useEffectiveBookingsForDate(selectedDate);
  const { data: regularBookings = [] } = useBookings(selectedDate);

  const validateClientBooking = useMemo(() => {
    return (
      clientId: string,
      timeSlot: string,
      workoutTypeId: string
    ): BookingConflict | null => {
      
      // Check for regular booking conflicts
      const regularConflict = regularBookings.find(booking => 
        booking.client_id === clientId &&
        booking.time_slot === timeSlot &&
        booking.workout_type_id === workoutTypeId
      );

      if (regularConflict) {
        return {
          type: 'regular',
          clientName: regularConflict.client 
            ? `${regularConflict.client.firstName} ${regularConflict.client.lastName}`
            : 'Unknown Client',
          timeSlot,
          workoutType: '', // Would need workout type name
          message: 'Client is already booked for this specific time slot'
        };
      }

      // Check for recurring booking conflicts
      const recurringConflict = effectiveBookings.find(booking => 
        booking.client_id === clientId &&
        booking.time_slot === timeSlot &&
        booking.workout_type_id === workoutTypeId &&
        booking.booking_type === 'recurring'
      );

      if (recurringConflict) {
        return {
          type: 'recurring',
          clientName: `${recurringConflict.client_first_name} ${recurringConflict.client_last_name}`,
          timeSlot,
          workoutType: '', // Would need workout type name
          message: 'Client already has a recurring booking for this day and time'
        };
      }

      return null;
    };
  }, [regularBookings, effectiveBookings]);

  const hasBookingConflict = useMemo(() => {
    return (clientId: string, timeSlot: string, workoutTypeId: string): boolean => {
      return validateClientBooking(clientId, timeSlot, workoutTypeId) !== null;
    };
  }, [validateClientBooking]);

  return {
    validateClientBooking,
    hasBookingConflict,
    effectiveBookings,
    regularBookings
  };
};